import React, {
    useContext, useMemo, useEffect, useCallback
} from 'react';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { ADOBE_TOPICS, SgDateUtil, SgSubmissionUtil } from 'saga-modules';
import { useValidation } from 'gw-portals-validation-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import SgQnBLobSpecificUtil from 'sg-qnb-lob-specific-util';
import SgCheckbox from 'sg-checkbox';
import { SG_AA_CTA_TEXT } from 'sg-adobe-analytics/AdobeAnalyticsConstants';
import _ from 'lodash';
import { publish } from '@jutro/events';
import metadata from './YourCreditExplained.metadata.json5';
import messages from './YourCreditExplained.messages';

function YourCreditExplained(props) {
    const {
        id,
        modelVM,
        updateData,
        showErrors,
        onNext,
        showBuyButtonSection,
        showCreditExplainedHeader,
        showWizardLoader,
        monthlyPP,
        onValidate: onValidateParent,
        adobeIdDocusign
    } = props;

    const { onValidate, isComponentValid } = useValidation(id);
    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    const {
        monthlyTotal, chargeForCreditWithApr, annualTotal
    } = useMemo(() => {
        return {
            annualTotal: SgSubmissionUtil.getAnnualPremium(modelVM),
            monthlyTotal: monthlyPP.total.amount.toFixed(2),
            chargeForCreditWithApr: monthlyPP.chargeForCreditWithApr_Ext.amount.toFixed(2)
        };
    }, [modelVM, monthlyPP]);

    const creditExplainedContent = useMemo(() => {
        const numberOfInstallments = monthlyPP.numberOfInstallments_Ext;
        const firstInstallmentDueDate = SgDateUtil.toStringShort(monthlyPP.firstInstalmentDate);

        const { isMotor, productName } = appConfig;
        const phoneNumber = SgQnBLobSpecificUtil.getYourCreditExplainedPhoneNumber();
        const appName = productName.split('-')[1];
        return (
            <>
                {showCreditExplainedHeader && (<h2>{translator(messages.YourCreditExplainedHeader)}</h2>)}
                <p>{translator(messages.ByOpting, { appName: appName })}</p>
                <p>
                    <span>{translator(messages.IfYouPurchase1)}</span>
                    <b>
                        £
                        {annualTotal}
                    </b>
                    <span>{translator(messages.IfYouPurchase2)}</span>
                    <b>
                        £
                        {monthlyTotal}
                    </b>
                    <span>{translator(messages.IfYouPurchase3)}</span>
                    <b>
                        £
                        {chargeForCreditWithApr}
                    </b>
                    <span>{translator(messages.IfYouPurchase4)}</span>
                </p>
                <p>{translator(messages.YourPreContact)}</p>
                <h3>{translator(messages.IsThisCreditSuitable)}</h3>
                <p>{translator(messages.TheCreditBeingProvided)}</p>
                <h3>{translator(messages.HowWillCreditWork)}</h3>
                <p>
                    <span>{translator(messages.InterestRateIsFixed, { numberOfInstallments: numberOfInstallments, })}</span>
                    <b>{firstInstallmentDueDate}</b>
                    <span>.</span>
                </p>
                <h3>{translator(messages.HowMuchWillCreditCost)}</h3>
                <p>{translator(messages.SeeRepresentativeExample)}</p>
                <h3 className="sgPageBreakBefore">{translator(messages.KeepingUpWithRepayments)}</h3>
                <p>{translator(messages.IfYouMissRepayment)}</p>
                <p>{translator(messages.MissingPayments)}</p>
                {isMotor && <p>{translator(messages.ObtainingCredits)}</p>}
                <h3>{translator(messages.RightOfWithdrawal)}</h3>
                <p>
                    {translator(messages.YouHaveRightToWithdrawal, {
                        phoneNumber: isMobile ? <a href={`tel:${phoneNumber.replaceAll(' ', '')}`}>{phoneNumber}</a> : phoneNumber
                    })}
                </p>
                <p>
                    {translator(messages.IfYouWithdraw, {
                        phoneNumber: isMobile ? <a href={`tel:${phoneNumber.replaceAll(' ', '')}`}>{phoneNumber}</a> : phoneNumber
                    })}
                </p>
            </>
        );
    }, [translator, monthlyPP, monthlyTotal, chargeForCreditWithApr, annualTotal, isMobile, showCreditExplainedHeader]);

    const handleDocuSignChange = useCallback((value) => {
        const newModelVM = _.clone(modelVM);
        _.set(newModelVM, 'isDocuSign_Ext.value', value || undefined);
        updateData(newModelVM);
        if (value) {
            publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: adobeIdDocusign, value: value });
        } else {
            publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: adobeIdDocusign, value: undefined });
        }
    }, [modelVM, updateData, adobeIdDocusign]);

    const overrideProps = {
        CreditExplainedTopSection: {
            content: creditExplainedContent
        },
        payNowLoader: {
            visible: showWizardLoader
        },
        payingByInstalmentsBuyButtonSection: {
            visible: showBuyButtonSection
        },
        payingByInstalmentsPayNowButton: {
            visible: !showWizardLoader,
            name: 'payingByInstalmentsPayNowButton',
            onClick: onNext,
            adobeId: SG_AA_CTA_TEXT.ASSUMPTIONS_MONTHLY_TEXT
        },
        docuSignCheckbox: {
            path: 'isDocuSign_Ext',
            selectedValue: modelVM.isDocuSign_Ext.value,
            onValueChange: handleDocuSignChange,
            requiredFieldValidationMessage: translator(messages.docuSignValidationMessage),
            name: translator(messages.docuSignCheckboxLabel)
        }
    };

    const resolvers = {
        resolveComponentMap: {
            SgCheckbox
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={modelVM}
            overrideProps={overrideProps}
            onValidationChange={onValidate}
            onModelChange={updateData}
            showErrors={showErrors}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}
YourCreditExplained.defaultProps = {
    showCreditExplainedHeader: true
};
export default YourCreditExplained;
